import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { TrendingDown, AlertTriangle, Calendar, BarChart3, Shield, Zap, Target, Wallet, ArrowRight } from 'lucide-react';
import { tradingRules } from '../mockData';
import { useLanguage } from '../context/LanguageContext';

const iconMap = {
  TrendingDown,
  AlertTriangle,
  Calendar,
  BarChart3,
  Shield,
  Zap,
  Target,
  Wallet
};

const TradingRules = () => {
  const [selectedPlan, setSelectedPlan] = useState('instant');
  const { t } = useLanguage();
  const rules = selectedPlan === 'instant' ? tradingRules.instant : tradingRules.oneStep;
  const isInstant = selectedPlan === 'instant';

  return (
    <section id="rules" style={{
      padding: '120px 24px',
      background: 'var(--bg-primary)',
      position: 'relative'
    }}>
      <div style={{
        maxWidth: '1400px',
        margin: '0 auto'
      }}>
        {/* Section Header */}
        <div style={{
          textAlign: 'center',
          marginBottom: '64px'
        }}>
          <h2 style={{
            fontSize: '48px',
            fontWeight: 700,
            marginBottom: '16px',
            color: 'var(--text-primary)'
          }} className="display-md">
            {t('tradingRules.title')}
          </h2>
          <p style={{
            fontSize: '18px',
            color: 'var(--text-secondary)',
            maxWidth: '700px',
            margin: '0 auto 40px'
          }} className="body-lg">
            {t('tradingRules.subtitle')}
          </p>

          {/* Plan Type Selector */}
          <div style={{
            display: 'inline-flex',
            background: 'var(--bg-secondary)',
            borderRadius: '12px',
            padding: '4px',
            border: '1px solid var(--border-primary)'
          }}>
            <button
              onClick={() => setSelectedPlan('instant')}
              style={{
                padding: '12px 28px',
                background: selectedPlan === 'instant' ? 'var(--accent-gold)' : 'transparent',
                color: selectedPlan === 'instant' ? 'var(--bg-primary)' : 'var(--text-secondary)',
                border: 'none',
                borderRadius: '8px',
                fontSize: '15px',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              ⚡ INSTANT
            </button>
            <button
              onClick={() => setSelectedPlan('oneStep')}
              style={{
                padding: '12px 28px',
                background: selectedPlan === 'oneStep' ? 'var(--accent-gold)' : 'transparent',
                color: selectedPlan === 'oneStep' ? 'var(--bg-primary)' : 'var(--text-secondary)',
                border: 'none',
                borderRadius: '8px',
                fontSize: '15px',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              🎯 1 STEP
            </button>
          </div>
        </div>

        {/* Rules Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: '24px'
        }}>
          {rules.map((rule) => {
            const IconComponent = iconMap[rule.icon];
            return (
              <div
                key={rule.id}
                style={{
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: '20px',
                  padding: '40px 32px',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  overflow: 'hidden'
                }}
                className="card hover-lift"
              >
                {/* Top accent line */}
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '3px',
                  background: isInstant 
                    ? 'linear-gradient(90deg, var(--accent-gold) 0%, var(--accent-gold-hover) 100%)'
                    : 'linear-gradient(90deg, var(--accent-blue) 0%, var(--accent-blue-hover) 100%)',
                  opacity: 0.7
                }} />

                {/* Icon */}
                <div style={{
                  width: '56px',
                  height: '56px',
                  background: isInstant ? 'rgba(212, 175, 55, 0.1)' : 'rgba(14, 165, 233, 0.1)',
                  borderRadius: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: isInstant ? 'var(--accent-gold)' : 'var(--accent-blue)',
                  marginBottom: '24px'
                }}>
                  <IconComponent size={28} />
                </div>

                {/* Title */}
                <h3 style={{
                  fontSize: '22px',
                  fontWeight: 600,
                  marginBottom: '12px',
                  color: 'var(--text-primary)'
                }} className="h3">
                  {rule.title}
                </h3>

                {/* Description */}
                <p style={{
                  fontSize: '16px',
                  lineHeight: 1.7,
                  color: 'var(--text-muted)'
                }} className="body-md">
                  {rule.description}
                </p>
              </div>
            );
          })}
        </div>

        {/* View More Button */}
        <div style={{
          textAlign: 'center',
          marginTop: '48px'
        }}>
          <Link
            to="/trading-rules"
            data-testid="view-more-rules-btn"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '14px 32px',
              background: 'transparent',
              border: '2px solid var(--accent-gold)',
              borderRadius: '12px',
              color: 'var(--accent-gold)',
              fontSize: '15px',
              fontWeight: 600,
              textDecoration: 'none',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = 'var(--accent-gold)';
              e.currentTarget.style.color = 'var(--bg-primary)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = 'var(--accent-gold)';
            }}
          >
            View More
            <ArrowRight size={18} />
          </Link>
        </div>

        {/* Info Banner */}
        <div style={{
          marginTop: '64px',
          background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.1) 0%, rgba(14, 165, 233, 0.1) 100%)',
          border: '1px solid var(--border-primary)',
          borderRadius: '20px',
          padding: '48px 40px',
          textAlign: 'center'
        }}>
          <h3 style={{
            fontSize: '28px',
            fontWeight: 600,
            marginBottom: '16px',
            color: 'var(--text-primary)'
          }}>
            {isInstant ? '⚡ Instant Funding Advantage' : '🎯 One Step Challenge'}
          </h3>
          <p style={{
            fontSize: '16px',
            lineHeight: 1.7,
            color: 'var(--text-secondary)',
            maxWidth: '800px',
            margin: '0 auto 24px'
          }}>
            {isInstant
              ? 'Get immediate access to live funded accounts. Trade Deriv Synthetic Indices 24/7 with institutional capital. Stricter risk management ensures long-term sustainability and consistent performance.'
              : 'Complete one evaluation phase with a 10% profit target, then receive your funded account. More flexible rules with no consistency requirements. Perfect for traders who want to prove their skills first.'
            }
          </p>
          <button
            onClick={() => window.location.href = '#plans'}
            className="btn-primary"
            style={{
              padding: '14px 32px'
            }}
          >
            View Account Plans
          </button>
        </div>
      </div>
    </section>
  );
};

export default TradingRules;